import { getPlantIcon } from './plantIconUtils';

/**
 * Builds the `seeds` table insert payload from a catalog_plants record.
 *
 * Used by every catalog-to-inventory insert path:
 *   - SeedInventory one-tap add (CatalogBrowser)
 *   - Global CatalogModal
 *   - GardenLayoutPlanner PlantPickerModal
 *
 * @param {string} userId      - The authenticated user's id
 * @param {object} catalogRecord - A row from catalog_plants
 * @returns {object} Insert payload ready for supabase.from('seeds').insert(...)
 */
export function buildSeedInsertFromCatalog(userId, catalogRecord) {
    return {
        user_id: userId,
        catalog_id: catalogRecord.id,
        plant: catalogRecord.plant,
        variety: catalogRecord.variety || null,
        category: catalogRecord.category || null,
        season: catalogRecord.season || null,
        harvest_season: catalogRecord.harvest_season || null,
        days_to_production: catalogRecord.days_to_production ?? null,
        preferred_start_method: catalogRecord.preferred_start_method || null,
        weeks_indoors_before_transplant: catalogRecord.weeks_indoors_before_transplant ?? null,
        spacing: catalogRecord.spacing || null,
        frost_tolerant: catalogRecord.frost_tolerant || false,
        sun_requirement: catalogRecord.sun_requirement || null,
        water_needs: catalogRecord.water_needs || null,
        direct_sow_weeks_before_last_frost: catalogRecord.direct_sow_weeks_before_last_frost ?? null,
        type: catalogRecord.type || 'seed',
        quantity: null,
        notes: null,
        need_to_buy: false,
        icon: getPlantIcon(catalogRecord.plant, null, catalogRecord.variety),
    };
}

/**
 * Parse an integer safely, returning null for NaN or non-finite values.
 * @param {*} val
 * @returns {number|null}
 */
const safeParseInt = (val) => {
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : null;
};

/**
 * Builds the `seeds` table update/insert payload from a form data object.
 *
 * Used by every form-based seed save path:
 *   - SeedInventory add/edit form
 *   - CatalogModalContext edit-details modal
 *   - GardenLayoutPlanner catalog-toast edit modal
 *
 * @param {object} fd           - Form data object (field names match the seeds table)
 * @param {string|null} existingIcon - Existing seed icon (used as fallback; auto-generated if null)
 * @returns {object} Payload ready for supabase .insert() or .update()
 */
export function buildSeedUpdateFromFormData(fd, existingIcon = null) {
    const isAnnual = (fd.type || 'seed') === 'seed';
    return {
        plant: fd.plant,
        variety: fd.variety || null,
        quantity: fd.quantity || null,
        season: isAnnual ? (fd.season || null) : null,
        category: fd.category || null,
        need_to_buy: fd.need_to_buy || false,
        notes: fd.notes || null,
        type: fd.type || 'seed',
        days_to_production: isAnnual && fd.days_to_production ? safeParseInt(fd.days_to_production) : null,
        weeks_indoors_before_transplant: isAnnual && fd.weeks_indoors_before_transplant ? safeParseInt(fd.weeks_indoors_before_transplant) : null,
        preferred_start_method: isAnnual ? (fd.preferred_start_method || null) : null,
        harvest_season: fd.harvest_season || null,
        years_to_production: !isAnnual ? (fd.years_to_production || null) : null,
        planned_start_date: isAnnual && fd.planned_start_date ? fd.planned_start_date : null,
        planned_transplant_date: isAnnual && fd.planned_transplant_date ? fd.planned_transplant_date : null,
        frost_tolerant: fd.frost_tolerant || false,
        spacing: fd.spacing || null,
        icon: fd.icon || existingIcon || getPlantIcon(fd.plant, null, fd.variety),
        sun_requirement: fd.sun_requirement || null,
        water_needs: fd.water_needs || null,
        direct_sow_weeks_before_last_frost:
            isAnnual && fd.direct_sow_weeks_before_last_frost !== '' && fd.direct_sow_weeks_before_last_frost != null
                ? safeParseInt(fd.direct_sow_weeks_before_last_frost)
                : null,
    };
}
